import React from 'react'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'

/**
 * The "Select Data" compact row (#968 Obs 10): Rows dropdown (checkbox list)
 * · Columns dropdown (checkbox list) · Switch Axis checkbox — replacing the
 * old row list + All/Latest/Specific radios.
 *
 * Deliberately POSITIONAL and widget-agnostic (Obs 11): options are labels in
 * table order, selections are 0-based positions, and duplicate labels are
 * therefore independently selectable — the fix for the per-brand "Cases where
 * you fill it" collision. An empty positions array means "all", which
 * refreshes with whatever occupies those positions after a data update.
 *
 * Shared component: the chart widget's TableSelectionEditor wraps it today;
 * the table widget editor is expected to reuse it (#968/#969 follow-up).
 */

export interface SelectDataDropdownProps {
  /** Axis noun, singular — 'Row' or 'Column'. Drives labels like "5 Rows". */
  noun: string
  /** Labels in table order. Position in this array IS the reference. */
  options: string[]
  /** Selected positions; empty means all. */
  selected: number[]
  onChange: (positions: number[]) => void
  /**
   * Fill the container instead of the compact row's fixed width. The table
   * widget stacks a checkbox under each dropdown and matches their widths
   * (#969 comment 2 item 4); the chart's single row does not.
   */
  fullWidth?: boolean
  /**
   * Offer a Select All / Deselect All entry at the top of the list (#1173
   * fault 2). Opt-in: CHART selection (this component's own SelectDataRow,
   * and the tables module's GraphConfigControls) wants it, but the TABLE
   * mode row/column panel (TableSelectDataPanel) is a different surface with
   * different semantics — bases stay selectable there — and #1173 does not
   * touch it. Default false so that surface is unaffected.
   */
  showSelectAll?: boolean
}

/**
 * Sentinel value for the Select All / Deselect All entry (#1173 fault 2).
 * `options` positions are always >= 0, so a negative value can never collide
 * with a real option and never leaks into an emitted position list.
 */
const SELECT_ALL_VALUE = -1

/**
 * One checkbox-list dropdown. The Select's value is the EFFECTIVE position
 * list (every position when "all"), so the checkboxes always reflect what the
 * widget renders; onChange canonicalizes a full set back to [] ("all").
 */
export const SelectDataDropdown: React.FC<SelectDataDropdownProps> = ({
  noun,
  options,
  selected,
  onChange,
  fullWidth = false,
  showSelectAll = false,
}) => {
  const allPositions = options.map((_, index) => index)
  const effective = selected.length > 0 ? selected : allPositions
  const isAll = effective.length === options.length
  const soleSelected = !isAll && effective.length === 1 ? effective[0] : undefined
  const lastOneReason = `A chart needs at least one ${noun.toLowerCase()} selected.`
  const label = `${noun}s`
  const labelId = React.useId()

  const handleChange = (value: number[]) => {
    if (value.includes(SELECT_ALL_VALUE)) {
      // Deselect All keeps only the first item in the list (Matt's decision
      // #2, #1173) — never an empty selection, and never a base: bases are
      // already excluded from `options` upstream (chartableAxis), so
      // position 0 is guaranteed to be a real data row.
      onChange(isAll ? (options.length > 0 ? [0] : []) : [])
      return
    }
    // Never allow an empty selection — a chart with nothing selected reads as
    // broken, and the user would have no checkbox left to undo it with.
    if (value.length === 0) return
    onChange(value.length === options.length ? [] : value.sort((a, b) => a - b))
  }

  return (
    <FormControl
      size="small"
      fullWidth={fullWidth}
      sx={fullWidth ? { minWidth: 150 } : { minWidth: 150, maxWidth: 220 }}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select<number[]>
        multiple
        labelId={labelId}
        label={label}
        value={effective}
        onChange={(e) => handleChange(e.target.value as number[])}
        renderValue={(value) =>
          value.length === options.length
            ? `All ${label}`
            : `${value.length} ${value.length === 1 ? noun : label}`
        }
        MenuProps={{
          PaperProps: { sx: { maxHeight: 320 } },
          // 'menu' (vs. the default 'selectedMenu') focuses the FIRST item
          // regardless of what's selected (#1173 fault 3): with everything
          // checked by default, 'selectedMenu' autofocuses the LAST selected
          // item, and browsers scroll a newly-focused element into view —
          // opening the list at the bottom. 'menu' keeps it open at the top,
          // where Select All lives.
          variant: 'menu',
        }}
      >
        {showSelectAll && options.length > 0 && (
          <MenuItem value={SELECT_ALL_VALUE} dense>
            <Checkbox size="small" checked={isAll} indeterminate={selected.length > 0 && !isAll} />
            {/* "Deselect All" could never empty the list (a chart needs one
                row), so the entry says what it does (Arun, Sky, 12 Oct). */}
            <ListItemText
              primary={isAll ? 'Deselect' : 'Select All'}
              secondary={isAll ? `Keeps the first ${noun.toLowerCase()}: a chart needs at least one.` : undefined}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </MenuItem>
        )}
        {options.map((optionLabel, position) => (
          <MenuItem
            key={position}
            value={position}
            dense
            // The one item left cannot be unticked; say why on hover instead
            // of the click silently doing nothing.
            title={soleSelected === position ? lastOneReason : undefined}
            aria-description={soleSelected === position ? lastOneReason : undefined}
          >
            <Checkbox size="small" checked={isAll || selected.includes(position)} />
            <ListItemText
              primary={<Typography variant="body2">{optionLabel}</Typography>}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

/**
 * Single-select column dropdown for the one-column chart types (#1172):
 * bar/column/pie plot exactly one column, so a multi checkbox list here
 * claimed "6 Columns" while the chart plotted one. The value is the single
 * selected display position (falling back to the first option, which is what
 * the generator plots when nothing is stored).
 */
const SingleColumnDropdown: React.FC<{
  options: string[]
  selected: number[]
  onChange: (positions: number[]) => void
}> = ({ options, selected, onChange }) => {
  const labelId = React.useId()
  const value = selected.length >= 1 ? selected[0] : 0
  return (
    <FormControl size="small" sx={{ minWidth: 150, maxWidth: 220 }}>
      <InputLabel id={labelId}>Column</InputLabel>
      <Select<number>
        labelId={labelId}
        label="Column"
        value={options.length > 0 ? Math.min(value, options.length - 1) : ''}
        onChange={(e) => onChange([Number(e.target.value)])}
        MenuProps={{ PaperProps: { sx: { maxHeight: 320 } }, variant: 'menu' }}
      >
        {options.map((optionLabel, position) => (
          <MenuItem key={position} value={position} dense>
            <Typography variant="body2">{optionLabel}</Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export interface SelectDataRowProps {
  /** Row labels in table order. */
  rowOptions: string[]
  /** Column labels in table order. */
  columnOptions: string[]
  /** Selected row positions; empty means all. */
  rowPositions: number[]
  /** Selected column positions; empty means all. */
  columnPositions: number[]
  switchAxis: boolean
  onRowPositionsChange: (positions: number[]) => void
  onColumnPositionsChange: (positions: number[]) => void
  onSwitchAxisChange: (switchAxis: boolean) => void
  /** One-column chart types (#1172): Columns becomes a single selector. */
  singleColumn?: boolean
}

export const SelectDataRow: React.FC<SelectDataRowProps> = ({
  rowOptions,
  columnOptions,
  rowPositions,
  columnPositions,
  switchAxis,
  onRowPositionsChange,
  onColumnPositionsChange,
  onSwitchAxisChange,
  singleColumn = false,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
    <SelectDataDropdown
      noun="Row"
      options={rowOptions}
      selected={rowPositions}
      onChange={onRowPositionsChange}
      showSelectAll
    />
    {singleColumn ? (
      <SingleColumnDropdown
        options={columnOptions}
        selected={columnPositions}
        onChange={onColumnPositionsChange}
      />
    ) : (
      <SelectDataDropdown
        noun="Column"
        options={columnOptions}
        selected={columnPositions}
        onChange={onColumnPositionsChange}
        showSelectAll
      />
    )}
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={switchAxis}
          onChange={(e) => onSwitchAxisChange(e.target.checked)}
        />
      }
      label={<Typography variant="body2">Switch Axis</Typography>}
    />
  </Box>
)

export default SelectDataRow
